/**
 * What this middleware reads of floating-ui's state.
 *
 * Declared rather than imported: floating-ui reaches this repo through fe-core
 * rather than as a dependency of its own. Asking for less than the real state
 * carries is what keeps it assignable — the real one has these and more.
 */
type Positioning = {
  placement: string
  rects: {
    reference: { x: number; width: number; height: number }
    floating: { width: number; height: number }
  }
  y: number
}

/**
 * Popover middleware.
 *
 * Precondition: Popover option `placement` is 'bottom-end'.
 *
 * Puts the overlay on its row, right edges aligned.
 *
 * `bottom-end` lines the overlay's right edge up with the cell's but leaves it
 * under the row, so this lifts it back on, centred, and the overlay covers the
 * panel instead of pushing the list around.
 *
 * `x` is restated rather than left to the placement because `Popover` runs
 * `shift` ahead of this, and the cell's own clipping is one of the boundaries
 * it shifts away from — an overlay wider than its cell gets nudged off the
 * alignment that is the point of it. Nothing is lost: the column is pinned at
 * the grid's right edge and the overlay opens leftward over the row.
 *
 * Only `x` comes from `shift`. It defaults to `crossAxis: false`, and on a
 * `bottom`/`top` placement the main axis is the horizontal one, so it never
 * wrote the `y` returned here.
 *
 * Both vertical directions are handled because `Popover` runs `flip` first: a
 * row near the bottom of the viewport is placed `top-end` instead, and centring
 * it means moving the other way. Either way it lands in the same place.
 */
export const coverTheCell = {
  name: 'coverTheCell',
  fn: ({ placement, rects, y }: Positioning) => {
    const distance = (rects.reference.height + rects.floating.height) / 2

    return {
      x: rects.reference.x + rects.reference.width - rects.floating.width,
      y: placement.startsWith('top') ? y + distance : y - distance,
    }
  },
}
